import { useEffect, useMemo, useRef } from "react";
import { Mail } from "lucide-react";

const HERO_IMG =
  "https://customer-assets-agu9un31.emergentagent.net/job_genesis-prds-site/artifacts/uwndj18z_poster1.png";

const ORB_COUNT = 9;

export default function Hero() {
  const sectionRef = useRef(null);
  const orbRefs = useRef([]);
  const mouse = useRef({ x: -9999, y: -9999 });

  const orbs = useMemo(() => {
    const rand = (a, b) => a + Math.random() * (b - a);
    const maxR = Math.min(100, (typeof window !== "undefined" ? window.innerWidth : 1200) * 0.07);
    const placed = [];
    for (let i = 0; i < ORB_COUNT; i++) {
      const r = rand(26, maxR);
      let x = 0, y = 0, tries = 0;
      do {
        x = rand(r + 10, (typeof window !== "undefined" ? window.innerWidth : 1200) - r - 10);
        y = rand(r + 10, (typeof window !== "undefined" ? window.innerHeight : 800) - r - 10);
        tries++;
      } while (tries < 60 && placed.some((p) => Math.hypot(p.x - x, p.y - y) < p.r + r + 24));
      placed.push({
        r, x, y,
        vx: (Math.random() < 0.5 ? -1 : 1) * rand(0.25, 0.6),
        vy: (Math.random() < 0.5 ? -1 : 1) * rand(0.2, 0.5),
        blur: i % 3 === 2 ? 2 : 0,
      });
    }
    return placed;
  }, []);

  useEffect(() => {
    const el = sectionRef.current;
    let W = el.clientWidth;
    let H = el.clientHeight;
    let raf;

    const step = () => {
      for (const o of orbs) {
        const dxm = o.x - mouse.current.x;
        const dym = o.y - mouse.current.y;
        const dm = Math.hypot(dxm, dym);
        if (dm < 200 && dm > 0.01) {
          const f = ((200 - dm) / 200) * 0.03;
          o.vx += (dxm / dm) * f;
          o.vy += (dym / dm) * f;
        }

        o.x += o.vx;
        o.y += o.vy;

        if (o.x < o.r) { o.x = o.r; o.vx = Math.abs(o.vx); }
        if (o.x > W - o.r) { o.x = W - o.r; o.vx = -Math.abs(o.vx); }
        if (o.y < o.r) { o.y = o.r; o.vy = Math.abs(o.vy); }
        if (o.y > H - o.r) { o.y = H - o.r; o.vy = -Math.abs(o.vy); }

        const speed = Math.hypot(o.vx, o.vy);
        if (speed > 0.9) { o.vx = (o.vx / speed) * 0.9; o.vy = (o.vy / speed) * 0.9; }
        else if (speed < 0.25 && speed > 0.001) { o.vx = (o.vx / speed) * 0.25; o.vy = (o.vy / speed) * 0.25; }
      }

      for (let i = 0; i < orbs.length; i++) {
        for (let j = i + 1; j < orbs.length; j++) {
          const a = orbs[i], b = orbs[j];
          const dx = b.x - a.x, dy = b.y - a.y;
          const dist = Math.hypot(dx, dy);
          const minDist = a.r + b.r;
          if (dist > 0.01 && dist < minDist) {
            const nx = dx / dist, ny = dy / dist;
            const overlap = (minDist - dist) / 2;
            a.x -= nx * overlap; a.y -= ny * overlap;
            b.x += nx * overlap; b.y += ny * overlap;
            const m1 = a.r * a.r, m2 = b.r * b.r;
            const vn1 = a.vx * nx + a.vy * ny;
            const vn2 = b.vx * nx + b.vy * ny;
            const nvn1 = (vn1 * (m1 - m2) + 2 * m2 * vn2) / (m1 + m2);
            const nvn2 = (vn2 * (m2 - m1) + 2 * m1 * vn1) / (m1 + m2);
            a.vx += (nvn1 - vn1) * nx; a.vy += (nvn1 - vn1) * ny;
            b.vx += (nvn2 - vn2) * nx; b.vy += (nvn2 - vn2) * ny;
          }
        }
      }

      orbs.forEach((o, i) => {
        const node = orbRefs.current[i];
        if (node) node.style.transform = `translate(${o.x - o.r}px, ${o.y - o.r}px)`;
      });
      raf = requestAnimationFrame(step);
    };

    raf = requestAnimationFrame(step);
    const onResize = () => { W = el.clientWidth; H = el.clientHeight; };
    window.addEventListener("resize", onResize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", onResize); };
  }, [orbs]);

  const onMouseMove = (e) => {
    const rect = sectionRef.current.getBoundingClientRect();
    mouse.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  return (
    <section
      id="top"
      ref={sectionRef}
      data-testid="hero-section"
      onMouseMove={onMouseMove}
      onMouseLeave={() => { mouse.current = { x: -9999, y: -9999 }; }}
      className="relative h-screen flex flex-col items-center justify-center overflow-hidden px-6"
    >
      <h1 className="sr-only">Drink Blue Balls — World's Best Hydration, Coconut Water</h1>

      <div className="absolute inset-0 hero-glow" aria-hidden="true" />

      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        {orbs.map((o, i) => (
          <div
            key={i}
            ref={(el) => (orbRefs.current[i] = el)}
            className="orb"
            style={{
              width: o.r * 2,
              height: o.r * 2,
              top: 0,
              left: 0,
              animation: "none",
              filter: o.blur ? `blur(${o.blur}px)` : undefined,
              transform: `translate(${o.x - o.r}px, ${o.y - o.r}px)`,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 animate-rise" style={{ animationDelay: "0.15s" }}>
        <div className="absolute inset-0 hero-glow scale-125" aria-hidden="true" />
        <div className="relative animate-float">
          <img
            data-testid="hero-product-image"
            src={HERO_IMG}
            alt="Drink Blue Balls coconut water can with gold Egyptian artwork"
            className="h-[72vh] sm:h-[80vh] lg:h-[84vh] w-auto max-w-[92vw] object-contain rounded-2xl shadow-[0_50px_160px_rgba(29,78,216,0.55)]"
          />
        </div>
      </div>

      <div className="absolute bottom-6 inset-x-0 z-10 flex justify-center pointer-events-none">
        <a
          data-testid="contact-email-link"
          href="mailto:hello@drinkblueballs.com"
          className="pointer-events-auto inline-flex items-center gap-2.5 text-sm sm:text-base tracking-[0.2em] text-slate-300 hover:text-[#F3E5AB] transition-colors duration-300 animate-rise"
          style={{ animationDelay: "0.35s" }}
        >
          <Mail className="w-4 h-4 text-[#D4AF37]" />
          hello@drinkblueballs.com
        </a>
      </div>
    </section>
  );
}
